import { useEffect, useRef } from 'react';

import { cn } from '@shared/components/ui/cn';

interface VoiceWaveformProps {
  /** While true, the waveform samples live mic loudness and scrolls. */
  active?: boolean;
  /** Instantaneous mic loudness in [0, 1]; polled each animation frame. */
  getLevel?: () => number;
  className?: string;
}

// Bar geometry (CSS px), matched to the AI-Chat-bot Figma waveform: thin 3px
// pills on a 7px pitch (3px bar + 4px gap), 32px track. Loudness drives height.
const BAR_W = 3;
const BAR_GAP = 4;
const BAR_PITCH = BAR_W + BAR_GAP;
const MAX_H = 22; // tallest bar at full loudness
const TRACK_H = 32;
/** Lead-in dot height: a tiny pinpoint for the un-reached track before speech. */
const LEADIN_H = 3;
/**
 * Floor height for the RECORDED region — the dot shown during a silent pause
 * AFTER speech started. Kept clearly above the lead-in dot so a mid-thought
 * pause still reads as "recording", just quiet.
 */
const RECORDED_MIN_H = 6;
/** Loudness above this counts as active speech (latches the recorded state). */
const SPEECH_THRESHOLD = 0.06;
/** Pixels the strip scrolls left per second. */
const SCROLL_PX_PER_SEC = BAR_PITCH * 7; // ~7 bars/sec — a calm glide

/** A captured sample: loudness + whether speech had begun by then. */
interface Sample {
  lvl: number;
  /** True once the user has spoken at least once at/before this sample. */
  live: boolean;
}

/**
 * Live recording visualizer, drawn on a canvas so the strip scrolls smoothly.
 *
 * Each frame the strip advances left; whenever it has travelled a full bar
 * pitch, the newest mic-loudness sample is pushed onto the right edge. Height
 * tracks loudness; COLOUR tracks recording state via a latched `hasSpoken`:
 *   - lead-in (no speech yet)        → short, light dot
 *   - recorded region (after speech) → dark, tall while loud and a SHORT DARK
 *     dot during pauses — one continuous "recording" band that never reverts
 *     to the lead-in look mid-speech (avoids the "gap" artifact).
 */
export function VoiceWaveform({ active = false, getLevel, className }: VoiceWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  /** Rolling sample window, oldest→newest (newest drawn at the right). */
  const samplesRef = useRef<Sample[]>([]);
  /** Latches true on the first detected speech; never resets mid-recording. */
  const hasSpokenRef = useRef(false);
  /** Sub-pixel scroll offset accumulated between bar pushes. */
  const offsetRef = useRef(0);
  const lastFrameRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

    // Theme-aware colours: read the element's resolved `color` (set to the
    // foreground token via className) for the recorded band; the lead-in dots
    // are the same colour at low alpha so it works in light and dark mode.
    const resolved = getComputedStyle(canvas).color || 'rgb(89, 89, 89)';
    const RECORDED_COLOR = resolved;
    const LEADIN_COLOR = resolved.startsWith('rgb(')
      ? resolved.replace('rgb(', 'rgba(').replace(')', ', 0.3)')
      : resolved.startsWith('rgba(')
        ? resolved.replace(/,\s*[\d.]+\)$/, ', 0.3)')
        : 'rgba(150, 150, 150, 0.4)';

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight || TRACK_H;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const barCount = Math.max(1, Math.ceil(w / BAR_PITCH) + 1);
      const samples = samplesRef.current;
      if (samples.length !== barCount) {
        samples.length = barCount;
        for (let i = 0; i < barCount; i += 1) {
          if (samples[i] == null) samples[i] = { lvl: 0, live: false };
        }
      }
    };
    resize();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    ro?.observe(canvas);

    const r = BAR_W / 2;
    const draw = () => {
      const samples = samplesRef.current;
      const cssW = canvas.clientWidth;
      const cssH = canvas.clientHeight || TRACK_H;
      ctx.clearRect(0, 0, cssW, cssH);
      const mid = cssH / 2;
      const baseX = cssW - BAR_W; // newest bar hugs the right edge
      for (let k = 0; k < samples.length; k += 1) {
        const x = baseX - k * BAR_PITCH - offsetRef.current;
        if (x + BAR_W < 0) break;
        const s = samples[samples.length - 1 - k] ?? { lvl: 0, live: false };
        // Recorded region: short dark line at rest that grows with loudness.
        // Lead-in: a tiny light dot.
        const h = s.live ? RECORDED_MIN_H + s.lvl * (MAX_H - RECORDED_MIN_H) : LEADIN_H;
        const y = mid - h / 2;
        ctx.fillStyle = s.live ? RECORDED_COLOR : LEADIN_COLOR;
        ctx.beginPath();
        if (typeof ctx.roundRect === 'function') {
          ctx.roundRect(x, y, BAR_W, h, r);
        } else {
          ctx.rect(x, y, BAR_W, h);
        }
        ctx.fill();
      }
    };

    if (!active || !getLevel) {
      // Idle/ended: flat row of light lead-in dots, state reset for next run.
      samplesRef.current.forEach((s) => {
        s.lvl = 0;
        s.live = false;
      });
      hasSpokenRef.current = false;
      offsetRef.current = 0;
      lastFrameRef.current = 0;
      draw();
      return () => ro?.disconnect();
    }

    const tick = (now: number) => {
      if (lastFrameRef.current === 0) lastFrameRef.current = now;
      const dt = Math.min(100, now - lastFrameRef.current); // clamp tab-stalls
      lastFrameRef.current = now;

      offsetRef.current += (SCROLL_PX_PER_SEC * dt) / 1000;
      while (offsetRef.current >= BAR_PITCH) {
        offsetRef.current -= BAR_PITCH;
        const lvl = getLevel();
        if (lvl > SPEECH_THRESHOLD) hasSpokenRef.current = true;
        const samples = samplesRef.current;
        samples.shift();
        // `live` latches once speech has started, so quiet pauses after the
        // first word stay in the dark recorded band instead of reverting.
        samples.push({ lvl, live: hasSpokenRef.current });
      }
      draw();
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      ro?.disconnect();
    };
  }, [active, getLevel]);

  return (
    <canvas
      ref={canvasRef}
      className={cn('h-8 w-full text-foreground', className)}
      aria-hidden="true"
    />
  );
}
